/**
 * Developer-tool state that lives next to the frame loop: movement event log,
 * speed graph, lab spawn point, input recording and replay. The Engine calls
 * the per-tick hooks; the DevPanel calls the actions. No gameplay code depends
 * on this module.
 */
import type { InputFrame } from '../input/InputFrame';
import type { MovementConfig } from '../core/Config';
import type { MovementRuleSet } from '../movement/MovementRules';
import type { Simulation } from '../simulation/Simulation';
import type { SpawnPoint } from '../world/LevelData';
import { MovementEventLog } from './MovementEvents';
import { InputRecorder, parseRecording, ReplayPlayer, type Recording } from './Recorder';
import { SpeedGraph } from './SpeedGraph';

/** What the dev tools need from the browser host. */
export interface DevHost {
  readonly sim: Simulation;
  readonly rules: MovementRuleSet;
  /** Rebuilds the simulation with these movement values (validated; throws on invalid). */
  applyMovement(movement: Readonly<MovementConfig>): void;
  /** Rule enablement or parameters changed outside the regular toggles. */
  rulesChanged(): void;
  /** Current view yaw (radians). */
  viewYaw(): number;
  /** Restart fixed-step timing (replay start while paused). */
  resetClock(): void;
  say(message: string): void;
}

export type ReplaySpeed = 1 | 0.5 | 0.25;

const SPAWN_KEY = 'xernous.labSpawn.v1';
const RAD = 180 / Math.PI;

export class DevTools {
  readonly log: MovementEventLog;
  readonly graph = new SpeedGraph();
  readonly recorder = new InputRecorder();
  lastRecording: Recording | null = null;
  replay: ReplayPlayer | null = null;
  replaySpeed: ReplaySpeed = 1;
  private replayAccum = 0;
  private stepRequests = 0;
  spawn: SpawnPoint | null;

  constructor(private readonly host: DevHost) {
    this.log = new MovementEventLog(host.sim);
    this.spawn = loadSpawn(host.sim.level.data.id);
  }

  /**
   * Movement parameters or rules changed. A recording spanning the change
   * could never be reproduced, and a running replay no longer replays the
   * recorded physics, so both stop with an explanation.
   */
  configurationChanged(): void {
    if (this.recorder.recording) {
      this.recorder.end(this.host.sim);
      this.host.say('Recording discarded: movement configuration changed while recording');
    }
    if (this.replay && this.replay.status !== 'finished') {
      this.replay = null;
      this.host.say('Replay stopped: movement configuration changed');
    }
  }

  // ------------------------------------------------------------ per tick

  get replayActive(): boolean {
    return this.replay !== null && this.replay.status !== 'finished';
  }

  /** Converts clock ticks into replay ticks (pause, slow motion, single step). */
  replayBudget(clockTicks: number): number {
    const r = this.replay;
    if (!r) return 0;
    const remaining = r.total - r.cursor;
    if (r.status === 'paused') {
      const n = Math.min(this.stepRequests, remaining);
      this.stepRequests = 0;
      return n;
    }
    this.replayAccum += clockTicks * this.replaySpeed;
    const n = Math.floor(this.replayAccum);
    this.replayAccum -= n;
    // Never step past the recording with live input in the same frame.
    return Math.min(n, remaining);
  }

  /** Input for this tick when replaying, else null (use live input). */
  replayInput(): InputFrame | null {
    return this.replay?.next() ?? null;
  }

  beforeStep(input: Readonly<InputFrame>): void {
    if (this.recorder.recording && !this.recorder.capture(input)) {
      this.stopRecording();
      this.host.say('Recording stopped: 120 s limit');
    }
  }

  afterStep(): void {
    const s = this.host.sim.state;
    this.log.observe();
    this.graph.push(s.tick, Math.hypot(s.velocity.x, s.velocity.z), s.velocity.y, s.grounded);
    const r = this.replay;
    if (r) {
      r.afterStep();
      if (r.status === 'finished') {
        this.host.say(r.matched ? 'Replay finished: end state matches the recording' : 'Replay finished: end state DIFFERS from the recording');
      }
    }
  }

  // ------------------------------------------------------------ spawn

  placeSpawnHere(): boolean {
    const s = this.host.sim.state;
    if (!s.alive || !s.grounded) {
      this.host.say('Stand on safe ground to place the lab spawn');
      return false;
    }
    this.spawn = { position: [s.position.x, s.position.y + 0.02, s.position.z], yawDeg: this.host.viewYaw() * RAD };
    saveSpawn(this.host.sim.level.data.id, this.spawn);
    this.host.say('Lab spawn placed (G to return)');
    return true;
  }

  gotoSpawn(): void {
    this.enterPractice();
    this.host.sim.placeAtSpawn(this.spawn ?? this.host.sim.level.data.start);
    this.host.say(this.spawn ? 'At lab spawn' : 'No lab spawn set: at track start');
  }

  /** Teleports to a level waypoint and makes it the lab spawn (so G and new recordings start there). */
  gotoPoint(spawn: SpawnPoint): void {
    this.spawn = { position: [...spawn.position], yawDeg: spawn.yawDeg };
    saveSpawn(this.host.sim.level.data.id, this.spawn);
    this.gotoSpawn();
  }

  clearSpawn(): void {
    this.spawn = null;
    saveSpawn(this.host.sim.level.data.id, null);
  }

  // ------------------------------------------------------------ recording / replay

  startRecording(): void {
    this.replay = null;
    this.enterPractice();
    this.recorder.begin(this.host.sim, this.spawn ?? this.host.sim.level.data.start);
    this.host.say('Recording from spawn…');
  }

  stopRecording(): Recording | null {
    const rec = this.recorder.end(this.host.sim);
    if (rec) this.lastRecording = rec;
    return rec;
  }

  startReplay(rec: Recording | null = this.lastRecording): void {
    if (!rec) return;
    const sim = this.host.sim;
    const problems = rec.levelId === sim.level.data.id ? sim.spawnProblems(rec.spawn, 'recording spawn') : [`recording is for level "${rec.levelId}"`];
    if (problems.length > 0) {
      this.host.say(`Replay refused: ${problems.join('; ')}`);
      return;
    }
    if (this.recorder.recording) this.stopRecording();
    this.enterPractice();
    this.replay = new ReplayPlayer(sim, rec);
    this.replay.status = 'playing';
    this.replayAccum = 0;
    this.host.resetClock();
    if (!this.replay.configMatches) this.host.say('Replay uses a different movement configuration: it will diverge');
  }

  toggleReplayPause(): void {
    const r = this.replay;
    if (!r || r.status === 'finished') return;
    r.status = r.status === 'paused' ? 'playing' : 'paused';
  }

  stepReplay(): void {
    if (this.replay?.status === 'paused') this.stepRequests++;
  }

  stopReplay(): void {
    this.replay = null;
  }

  importRecording(json: string): Recording {
    const rec = parseRecording(JSON.parse(json) as unknown);
    this.lastRecording = rec;
    return rec;
  }

  dispose(): void {
    this.log.dispose();
  }

  /** Dev placements never run inside a recordable full-run attempt. */
  private enterPractice(): void {
    if (this.host.sim.mode === 'full-run') {
      this.host.sim.setMode('practice');
      this.host.say('Switched to practice: developer tools never produce recorded times');
    }
  }
}

function loadSpawn(levelId: string): SpawnPoint | null {
  try {
    const raw = JSON.parse(localStorage.getItem(`${SPAWN_KEY}.${levelId}`) ?? 'null') as unknown;
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as { position?: unknown; yawDeg?: unknown };
    const p = o.position;
    if (!Array.isArray(p) || p.length !== 3 || !p.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
    if (typeof o.yawDeg !== 'number' || !Number.isFinite(o.yawDeg)) return null;
    return { position: [p[0] as number, p[1] as number, p[2] as number], yawDeg: o.yawDeg };
  } catch {
    return null;
  }
}

function saveSpawn(levelId: string, spawn: SpawnPoint | null): void {
  try {
    if (spawn) localStorage.setItem(`${SPAWN_KEY}.${levelId}`, JSON.stringify(spawn));
    else localStorage.removeItem(`${SPAWN_KEY}.${levelId}`);
  } catch (err: unknown) {
    console.warn('Lab spawn could not be saved to this browser.', err);
  }
}
